/* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-call, typescript/no-unsafe-return -- polymorphic props inspector; runtime values are any */
import { SB } from './storybook-ui'
import { createElement, useEffect, useRef, useState } from 'react'
import { useIsMobile } from '../hooks/use-mobile'
import { textVariants } from '../components/text'
import type { StorySchemaDescriptor } from './story'
import type { StoryControlsEditorProps } from './story-controls'

const PANEL_MARGIN = 8

interface PanelPosition {
  x: number
  y: number
}

function clampPosition(
  pos: PanelPosition,
  size: { width: number; height: number },
) {
  const maxX = window.innerWidth - size.width - PANEL_MARGIN
  const maxY = window.innerHeight - size.height - PANEL_MARGIN
  return {
    x: Math.min(Math.max(pos.x, PANEL_MARGIN), Math.max(maxX, PANEL_MARGIN)),
    y: Math.min(Math.max(pos.y, PANEL_MARGIN), Math.max(maxY, PANEL_MARGIN)),
  }
}

function useDraggablePanel(panelRef: React.RefObject<HTMLDivElement | null>) {
  const [position, setPosition] = useState<PanelPosition | null>(null)
  const dragState = useRef<{
    startX: number
    startY: number
    originX: number
    originY: number
  } | null>(null)

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) throw new Error('Props editor panel is not mounted')
    const clampCurrentPosition = () => {
      const rect = panel.getBoundingClientRect()
      setPosition((current) =>
        current
          ? clampPosition(current, { width: rect.width, height: rect.height })
          : null,
      )
    }
    const resizeObserver = new ResizeObserver(clampCurrentPosition)
    resizeObserver.observe(panel)
    window.addEventListener('resize', clampCurrentPosition)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', clampCurrentPosition)
    }
  }, [panelRef])

  useEffect(() => {
    const onWindowPointerMove = (e: PointerEvent) => {
      if (!dragState.current || !panelRef.current) return
      const { startX, startY, originX, originY } = dragState.current
      const rect = panelRef.current.getBoundingClientRect()
      setPosition(
        clampPosition(
          {
            x: originX + (e.clientX - startX),
            y: originY + (e.clientY - startY),
          },
          { width: rect.width, height: rect.height },
        ),
      )
    }
    const onWindowPointerUp = () => {
      dragState.current = null
    }
    window.addEventListener('pointermove', onWindowPointerMove)
    window.addEventListener('pointerup', onWindowPointerUp)
    return () => {
      window.removeEventListener('pointermove', onWindowPointerMove)
      window.removeEventListener('pointerup', onWindowPointerUp)
    }
  }, [panelRef])

  const onDragHandlePointerDown = (e: React.PointerEvent) => {
    if (!panelRef.current) return
    const rect = panelRef.current.getBoundingClientRect()
    const origin = position ?? { x: rect.left, y: rect.top }
    setPosition(origin)
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: origin.x,
      originY: origin.y,
    }
  }

  return {
    position,
    dragHandleProps: {
      onPointerDown: onDragHandlePointerDown,
    },
  }
}

interface FieldInfo {
  type: string
  meta?: Record<string, any>
  defaultValue: unknown
  isOptional: boolean
  options?: readonly string[]
  element?: { type: string; options?: readonly string[] }
}

function getFieldInfo(fieldSchema: StorySchemaDescriptor): FieldInfo {
  let schema = fieldSchema
  let defaultValue: unknown
  let isOptional = false

  // unwrap wrappers (default, optional, nullable)
  while (
    schema.wrapper === 'default' ||
    schema.wrapper === 'optional' ||
    schema.wrapper === 'nullable'
  ) {
    if (schema.wrapper === 'default') {
      defaultValue = schema.default?.factory ? undefined : schema.default?.value
      schema = schema.schema ?? schema
    } else if (schema.wrapper === 'optional') {
      isOptional = true
      schema = schema.schema ?? schema
    } else if (schema.wrapper === 'nullable') {
      isOptional = true
      schema = schema.schema ?? schema
    }
  }

  const type = schema.type ?? 'unknown'
  const meta = schema.metadata

  // for enum
  const options = type === 'enum' ? schema.values : undefined

  // for array - get element info
  let element: FieldInfo['element']
  if (type === 'array' && schema.element) {
    const elemInfo = getFieldInfo(schema.element)
    element = {
      type: elemInfo.type,
      options: elemInfo.options,
    }
  }

  return { type, meta, defaultValue, isOptional, options, element }
}

export function extractDefaults(
  descriptor: StorySchemaDescriptor,
): Record<string, unknown> {
  const shape = getSchemaShape(descriptor)
  const defaults: Record<string, unknown> = {}

  for (const key in shape) {
    if (!Object.hasOwn(shape, key)) continue
    const info = getFieldInfo(shape[key])
    defaults[key] = info.defaultValue
  }

  return defaults
}

function getSchemaShape(descriptor: StorySchemaDescriptor) {
  if (descriptor.type !== 'object' || !descriptor.shape) {
    throw new Error('Storybook props editor schema must describe an object')
  }
  return descriptor.shape
}

function toLogfmt(value: unknown): string {
  if (value === null || value === undefined) return ''
  // oxlint-disable-next-line typescript/no-base-to-string -- value is confirmed non-object; primitives stringify correctly
  if (typeof value !== 'object') return String(value)

  if (Array.isArray(value)) {
    return value
      .map((v, i) => `[${i}]=${typeof v === 'object' ? '{...}' : v}`)
      .join(' ')
  }

  return Object.entries(value)
    .map(([k, v]) => `${k}=${typeof v === 'object' ? '{...}' : v}`)
    .join(' ')
}

function formatDateForInput(date: Date | undefined): string {
  if (!(date && date instanceof Date) || Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 16)
}

function parseDateFromInput(value: string): Date | undefined {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function SimpleStoryControlsEditor({
  componentName,
  descriptor,
  values,
  onChange,
  onReset,
  showToast,
  children,
}: StoryControlsEditorProps) {
  const isMobile = useIsMobile()
  // Start folded on mobile: the panel docks bottom-center and would otherwise cover
  // the story. Read the breakpoint once at init so no effect/setState is needed.
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  )
  const [dismissed, setDismissed] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const { position, dragHandleProps } = useDraggablePanel(panelRef)

  const shape = getSchemaShape(descriptor)
  const fields = Object.keys(shape)

  const updateField = (key: string, value: unknown) => {
    onChange({ ...values, [key]: value })
  }

  const copyProps = async () => {
    try {
      const code = JSON.stringify(values, null, 2)
      showToast('Props copied')
      await navigator.clipboard.writeText(code)
    } catch (error) {
      console.error('Failed to copy:', error)
      showToast('Failed to copy')
    }
  }

  const resetProps = () => {
    onReset()
    showToast('Props reset')
  }

  if (dismissed) return children

  return (
    <>
      {children}
      <div
        ref={panelRef}
        className={SB.cn(
          SB.tokens.color.surface,
          SB.tokens.color.border,
          'fixed z-50 w-fit min-w-52 max-w-[min(28rem,calc(100vw-1rem))] rounded-lg border shadow-lg',
          isMobile && 'bottom-2 left-1/2 -translate-x-1/2',
        )}
        style={
          isMobile
            ? undefined
            : {
                top: position?.y ?? 16,
                left: position?.x,
                right: position ? undefined : 16,
              }
        }
        data-testid="simple-story-controls"
      >
        <div
          className={SB.cn(
            'flex h-9 items-center justify-between gap-2 rounded-t-lg px-3 select-none',
            collapsed && 'rounded-b-lg',
            !isMobile && 'cursor-grab active:cursor-grabbing',
          )}
          {...(isMobile ? {} : dragHandleProps)}
        >
          <SB.Text
            size="label"
            tone="muted"
            className="min-w-0 flex-1 truncate font-normal"
            title={componentName}
          >
            {componentName}
          </SB.Text>
          <div className="flex justify-end gap-2">
            {collapsed ? (
              <SB.IconButton
                size="sm"
                semanticRole="secondary"
                tooltipSideOffset={5}
                onPointerDown={(e) => {
                  e.stopPropagation()
                }}
                onClick={() => {
                  setDismissed(true)
                }}
              >
                <SB.icons.X />
                Remove props editor
              </SB.IconButton>
            ) : (
              <>
                <SB.IconButton
                  size="sm"
                  semanticRole="secondary"
                  tooltipSideOffset={5}
                  onPointerDown={(e) => {
                    e.stopPropagation()
                  }}
                  onClick={() => {
                    void copyProps()
                  }}
                >
                  <SB.icons.Copy />
                  Copy props
                </SB.IconButton>
                <SB.IconButton
                  size="sm"
                  semanticRole="secondary"
                  tooltipSideOffset={5}
                  onPointerDown={(e) => {
                    e.stopPropagation()
                  }}
                  onClick={resetProps}
                >
                  <SB.icons.RotateCcw />
                  Reset
                </SB.IconButton>
              </>
            )}
            <SB.IconButton
              size="sm"
              semanticRole="secondary"
              tooltipSideOffset={5}
              onPointerDown={(e) => {
                e.stopPropagation()
              }}
              onClick={() => {
                setCollapsed((c) => !c)
              }}
            >
              <SB.icons.ChevronRight
                className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}
              />
              {collapsed ? 'Expand' : 'Collapse'}
            </SB.IconButton>
          </div>
        </div>

        {/* grid-rows 0fr→1fr animates height with no layout snap; content stays mounted */}
        <div
          className={SB.cn(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
        >
          <div
            className={SB.cn(
              SB.tokens.color.border,
              'min-h-0 overflow-hidden border-t',
            )}
          >
            <div className="mx-auto grid max-h-[70vh] w-fit max-w-full grid-cols-[max-content_minmax(0,max-content)] items-center gap-x-3 gap-y-1.5 overflow-y-auto px-3 py-1.5">
              {fields.map((key) => {
                const info = getFieldInfo(shape[key])
                const value = values[key]

                return (
                  <div key={key} className="contents">
                    <SB.Text
                      size="label"
                      tone="muted"
                      className="self-center text-right font-normal"
                    >
                      {key}
                    </SB.Text>
                    <div className="flex min-w-0 items-center">
                      <FieldInput
                        info={info}
                        value={value}
                        onChange={(v) => {
                          updateField(key, v)
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

interface FieldInputProps {
  info: FieldInfo
  value: unknown
  onChange: (value: unknown) => void
}

const unsetValue = '__props_editor_unset__'

// INVARIANT: Props-editor Select triggers fill their shared control column.
// Do not change this to intrinsic/content sizing when adjusting panel or grid layout.
const selectTriggerClassName = 'w-full max-w-full'

function getIconOption(
  info: FieldInfo,
  value: unknown,
): React.ComponentType<{ className?: string }> | undefined {
  if (info.meta?.type !== 'icon' || !Array.isArray(info.meta.icons)) {
    return undefined
  }
  return info.meta.icons.find(
    (Icon: React.ComponentType & { displayName?: string }) =>
      Icon.displayName === value,
  )
}

function IconOption({ info, value }: { info: FieldInfo; value: string }) {
  const Icon = getIconOption(info, value)
  return (
    <span className="flex items-center gap-1">
      {Icon && createElement(Icon, { className: 'size-4' })}
      {value.replace(/Icon$/u, '')}
    </span>
  )
}

function FieldInput({ info, value, onChange }: FieldInputProps) {
  // color picker — no design-system equivalent for a compact inline swatch.
  // Sized to match the checkbox (20px, 6px radius). Native swatch chrome is stripped
  // so the whole control is the color square.
  if (info.type === 'string' && info.meta?.type === 'color') {
    return (
      <input
        type="color"
        value={(value as string) ?? '#000000'}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className="size-5 cursor-pointer rounded-[6px] border-0 bg-transparent p-0 [&::-moz-color-swatch]:rounded-[6px] [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:rounded-[6px] [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
      />
    )
  }

  if (
    info.type === 'string' &&
    info.meta?.type === 'icon' &&
    Array.isArray(info.meta.icons)
  ) {
    const options = info.meta.icons
      .map(
        (Icon: React.ComponentType & { displayName?: string }) =>
          Icon.displayName,
      )
      .filter((name: string | undefined): name is string => name !== undefined)
    return (
      <SB.Select.Root
        minimal
        value={value ?? unsetValue}
        onValueChange={(v) => {
          onChange(v === unsetValue ? undefined : v)
        }}
      >
        <SB.Select.Trigger className={selectTriggerClassName}>
          <SB.Select.Value>
            {(v: string) =>
              v === unsetValue ? 'none' : <IconOption info={info} value={v} />
            }
          </SB.Select.Value>
        </SB.Select.Trigger>
        <SB.Select.Popup>
          <SB.Select.Item value={unsetValue}>none</SB.Select.Item>
          {options.map((option) => (
            <SB.Select.Item key={option} value={option}>
              <IconOption info={info} value={option} />
            </SB.Select.Item>
          ))}
        </SB.Select.Popup>
      </SB.Select.Root>
    )
  }

  // string
  if (info.type === 'string') {
    return (
      <SB.Input
        type="text"
        minimal
        className="w-auto max-w-full [&_[data-slot=input]]:field-sizing-content [&_[data-slot=input]]:w-auto"
        value={(value as string) ?? ''}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={info.isOptional ? 'none' : ''}
      />
    )
  }

  // number
  if (info.type === 'number') {
    return (
      <SB.Input
        type="number"
        minimal
        className="w-auto max-w-full [&_[data-slot=input]]:field-sizing-content [&_[data-slot=input]]:w-auto"
        // oxlint-disable-next-line typescript/no-base-to-string -- value is number at this point; String() is correct
        value={value === undefined ? '' : String(value)}
        onChange={(e) => {
          onChange(e.target.value === '' ? undefined : Number(e.target.value))
        }}
        placeholder={info.isOptional ? 'none' : ''}
      />
    )
  }

  // boolean
  if (info.type === 'boolean') {
    return (
      <SB.Checkbox
        checked={(value as boolean) ?? false}
        onCheckedChange={onChange}
      />
    )
  }

  // enum (select)
  if (info.type === 'enum' && info.options) {
    return (
      <SB.Select.Root
        minimal
        value={value ?? unsetValue}
        onValueChange={(v) => {
          onChange(v === unsetValue ? undefined : v)
        }}
      >
        <SB.Select.Trigger className={selectTriggerClassName}>
          <SB.Select.Value>
            {(v: string) =>
              v === unsetValue ? 'none' : <IconOption info={info} value={v} />
            }
          </SB.Select.Value>
        </SB.Select.Trigger>
        <SB.Select.Popup>
          {info.isOptional && (
            <SB.Select.Item value={unsetValue}>none</SB.Select.Item>
          )}
          {info.options.map((opt) => (
            <SB.Select.Item key={opt} value={opt}>
              <IconOption info={info} value={opt} />
            </SB.Select.Item>
          ))}
        </SB.Select.Popup>
      </SB.Select.Root>
    )
  }

  // date — no design-system equivalent for a compact inline datetime field
  if (info.type === 'date') {
    return (
      <input
        type="datetime-local"
        value={formatDateForInput(value as Date)}
        onChange={(e) => {
          onChange(parseDateFromInput(e.target.value))
        }}
        className={SB.cn(
          textVariants({ size: 'hint', tone: 'default' }),
          '-ml-1 h-6 min-w-0 w-auto max-w-full field-sizing-content border-0 bg-transparent px-1 focus:outline-none',
        )}
      />
    )
  }

  // array of enum (multi-select)
  if (
    info.type === 'array' &&
    info.element?.type === 'enum' &&
    info.element.options
  ) {
    const selectedValues = (value as string[]) ?? []
    return (
      <SB.Select.Root
        minimal
        multiple
        value={selectedValues}
        onValueChange={(v) => {
          onChange(v)
        }}
      >
        <SB.Select.Trigger className={selectTriggerClassName}>
          <SB.Select.Value placeholder="none" />
        </SB.Select.Trigger>
        <SB.Select.Popup>
          {info.element.options.map((opt) => (
            <SB.Select.Item key={opt} value={opt}>
              {opt}
            </SB.Select.Item>
          ))}
        </SB.Select.Popup>
      </SB.Select.Root>
    )
  }

  // array of primitives (comma-separated)
  if (
    info.type === 'array' &&
    (info.element?.type === 'string' || info.element?.type === 'number')
  ) {
    const arr = (value as (string | number)[]) ?? []
    const strValue = arr.join(', ')
    return (
      <SB.Input
        type="text"
        minimal
        className="w-auto max-w-full [&_[data-slot=input]]:field-sizing-content [&_[data-slot=input]]:w-auto"
        value={strValue}
        onChange={(e) => {
          const parts = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
          if (info.element?.type === 'number') {
            onChange(parts.map(Number))
          } else {
            onChange(parts)
          }
        }}
        placeholder="comma separated"
      />
    )
  }

  // object or array of objects (readonly logfmt)
  if (
    info.type === 'object' ||
    (info.type === 'array' && info.element?.type === 'object')
  ) {
    return (
      <SB.Text
        size="hint"
        tone="muted"
        className="block truncate"
        title={toLogfmt(value)}
      >
        {toLogfmt(value) || '(empty)'}
      </SB.Text>
    )
  }

  // fallback: readonly display
  return (
    <SB.Text size="hint" tone="muted" className="block truncate">
      {/* oxlint-disable-next-line typescript/no-base-to-string -- value is unknown fallback display; runtime values are demonstrably stringifiable */}
      {String(value ?? '')}
    </SB.Text>
  )
}
